import React, { useEffect, useRef } from 'react';
import { string, object } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../../util/reactIntl';

import { NamedLink } from '../../../components';
import css from './SectionHeroCustom.module.css';
import heroBackground from '../../../assets/images/hero.png';

// Set to true to reactivate the "Browse Services" button
const SHOW_SERVICES_BUTTON = false;

// Served from public/static (copied verbatim into the build, not bundled by webpack),
// so the file weight never enters the JS build and can be swapped without a rebuild.
const HERO_VIDEO_URL = '/static/hero-video.mp4';

const SectionHeroCustom = props => {
  const { className, rootClassName, currentUser } = props;
  const isLoggedIn = !!currentUser;
  const heroVideoRef = useRef(null);

  const classes = classNames(rootClassName || css.root, className);

  // React does not serialize `muted` into the server-rendered markup, so after
  // hydration the element can reach the browser unmuted and get its autoplay
  // blocked. Setting it imperatively is the same workaround TeamPage uses for
  // its dealer banner. The play() rejection is ignored on purpose: when a
  // browser still refuses (data saver, power saving), the poster remains.
  useEffect(() => {
    const videoEl = heroVideoRef.current;
    if (!videoEl) {
      return;
    }

    // The stylesheet already hides the video under reduced motion; skipping
    // play() here is what stops the file from being downloaded at all, since
    // preload="metadata" otherwise only fetches the header.
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      return;
    }

    videoEl.muted = true;
    const playPromise = videoEl.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  }, []);

  return (
    <section className={classes}>
      <div className={css.heroContent}>
        {/* Decorative background: muted + playsInline are what allow browsers to
            autoplay it, and hero.png is both the first paint and the fallback if
            autoplay is blocked or the user prefers reduced motion. */}
        <video
          ref={heroVideoRef}
          className={css.heroVideo}
          src={HERO_VIDEO_URL}
          poster={heroBackground}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
          tabIndex={-1}
        />
        <div className={css.scrim} />
        <div className={css.overlay}>
          <div className={css.textContent}>
            <h1 className={css.heroTitle}>
              <FormattedMessage id="SectionHeroCustom.title" />
            </h1>
            <p className={css.heroSubtitle}>
              <FormattedMessage id="SectionHeroCustom.subtitle" />
            </p>
            <div className={css.ctaContainer}>
              <NamedLink
                name="SearchPage"
                to={{}}
                className={classNames(css.ctaButton, css.primaryButton)}
              >
                <FormattedMessage id="SectionHeroCustom.searchProducts" />
              </NamedLink>
              {SHOW_SERVICES_BUTTON && (
                <NamedLink
                  name="ServicesSearchPage"
                  to={{}}
                  className={classNames(css.ctaButton, css.secondaryButton)}
                >
                  <FormattedMessage id="SectionHeroCustom.searchServices" />
                </NamedLink>
              )}
              <NamedLink
                name="BecomeASellerPage"
                className={classNames(css.ctaButton, css.tertiaryButton)}
              >
                <FormattedMessage id="SectionHeroCustom.applyToSell" />
              </NamedLink>
              {!isLoggedIn && (
                <NamedLink
                  name="SignupPage"
                  className={classNames(css.ctaButton, css.quaternaryButton)}
                >
                  <FormattedMessage id="SectionHeroCustom.createAccount" />
                </NamedLink>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

SectionHeroCustom.defaultProps = {
  className: null,
  rootClassName: null,
  currentUser: null,
};

SectionHeroCustom.propTypes = {
  className: string,
  rootClassName: string,
  currentUser: object,
};

export default SectionHeroCustom;
